import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import {
  useCampaignIpExclusionsQuery,
  useAddCampaignIpExclusionsMutation,
  useDeleteCampaignIpExclusionMutation,
} from '@/lib/query-options';
import type { CampaignIpExclusion } from '@/lib/apis';
import { Trash2, ShieldOff, Plus } from 'lucide-react';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { FieldError } from '@/components/glitch-ui/field-error';

interface CampaignIpExclusionsViewProps {
  campaignId: number;
  disabled?: boolean;
}

function parseIpAddresses(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((ip) => ip.trim())
    .filter((ip) => ip.length > 0);
}

export const CampaignIpExclusionsView: React.FC<CampaignIpExclusionsViewProps> = ({ campaignId, disabled = false }) => {
  const { toast } = useToast();
  const { data: exclusionsData, isLoading } = useCampaignIpExclusionsQuery(campaignId);
  const addMutation = useAddCampaignIpExclusionsMutation(campaignId);
  const deleteMutation = useDeleteCampaignIpExclusionMutation(campaignId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bulkIps, setBulkIps] = useState('');
  // Tell-on-click (#5857): Add stays enabled; a click with no valid IPs marks the field.
  const [ipsError, setIpsError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CampaignIpExclusion | null>(null);

  const exclusions = exclusionsData?.ip_exclusions || [];

  const columns: ColumnDef<CampaignIpExclusion>[] = [
    {
      accessorKey: 'ip_address',
      header: 'IP Address',
      sortingFn: 'alphanumeric',
      cell: ({ row }) => (
        <span className='text-sm font-mono font-medium text-gray-900'>{row.original.ip_address}</span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      header: () => <span className='text-right block'>Actions</span>,
      cell: ({ row }) => (
        <div className='flex justify-end'>
          <IconButton
            variant='destructive'
            aria-label='Remove IP exclusion'
            onClick={() => setDeleteTarget(row.original)}
            disabled={disabled}
          >
            <Trash2 className='h-4 w-4' />
          </IconButton>
        </div>
      ),
    },
  ];

  const resetForm = () => {
    setBulkIps('');
    setIpsError('');
    setIsModalOpen(false);
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    const ips = parseIpAddresses(bulkIps);
    // Tell-on-click (#5857): mark the field instead of a silently disabled button.
    if (ips.length === 0) {
      setIpsError(bulkIps.trim() ? 'No valid IP addresses found' : 'Required');
      return;
    }

    try {
      await addMutation.mutateAsync(ips);
      toast({ description: `Added ${ips.length} IP exclusion(s)` });
      resetForm();
    } catch (err) {
      toast({
        description: err instanceof Error ? err.message : 'Failed to add IP exclusions',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      toast({
        description: err instanceof Error ? err.message : 'Failed to delete IP exclusion',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div>
        <h2 className='text-xl font-semibold text-gray-900'>IP Exclusions</h2>
        <p className='text-sm text-gray-600 mt-1'>Loading IP exclusions...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>IP Exclusions</h2>
          <p className='text-sm text-gray-600 mt-1'>Block IP addresses from seeing ads in this campaign.</p>
          <p className='text-xs text-gray-500 mt-1'>
            To block IPs across all campaigns, use{' '}
            <Link to='/assets' hash='google/ip-exclusions' className='text-[#290BD9] hover:text-[#2309C5]'>account-wide IP exclusions</Link> in Assets &rarr; Google.
          </p>
        </div>
        <Button variant='outline' className='text-gray-900' onClick={openAddModal} disabled={disabled}>
          <Plus className='h-4 w-4 mr-2' />
          Add IPs
        </Button>
      </div>

      {/* Table */}
      <DataTable
        data={exclusions}
        columns={columns}
        variant='flush'
        pageSize={999}
        initialSorting={[{ id: 'ip_address', desc: false }]}
        emptyIcon={ShieldOff}
        emptyTitle='No IP Exclusions'
        emptyDescription='Block IP addresses to prevent specific users from seeing ads in this campaign.'
      />

      {/* Add Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[500px]' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add IP Exclusions</DialogTitle>
          </DialogHeader>

          <div className='py-4 space-y-4'>
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <Label>IP Addresses</Label>
                <FieldError message={ipsError} />
              </div>
              <Textarea
                value={bulkIps}
                onChange={(e) => { setBulkIps(e.target.value); setIpsError(''); }}
                placeholder={'Enter IPs, one per line or comma-separated\n192.168.1.1\n203.0.113.5, 198.51.100.0/24'}
                className='min-h-[150px] text-gray-900 font-mono'
                error={!!ipsError}
                disabled={disabled || addMutation.isPending}
                autoFocus
              />
              <p className='text-xs text-gray-500'>
                Individual IPv4/IPv6 addresses are supported, along with /24 and /32 IPv4 masks (e.g. 198.51.100.0/24).
              </p>
            </div>
          </div>

          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={disabled || addMutation.isPending}
              className='bg-[#290BD9] hover:bg-[#2309C5]'
            >
              {addMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConformationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title='Remove IP Exclusion'
        description={`Are you sure you want to unblock "${deleteTarget?.ip_address}"? Ads will become visible to this IP again.`}
        confirmText='Remove'
        cancelText='Cancel'
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
